// Fila „Profil” din Profilul elevului, din tiparele aplicației (components/UI): câmpurile într-un
// InputUI.Group de 2 coloane (colțurile de afară rotunjite, ca în Tipare), comutatoarele ca
// plăcuțe InputUI cu ToggleUI, erorile StatusUI, butoanele ButtonUI.Row (ștergerea cu confirmarea
// pe loc a ButtonUI). Fără drept de editare: aceleași plăcuțe, doar de citit.
import React, { useContext } from "react";

import { ACCOUNT_DELETE_CONFIRM_TITLE } from "../../../constants/accountDeletion";
import { ButtonUI, InputUI, ToggleUI } from "../../UI";
import { StudentInfoContext } from "./StudentInfoContext";
import { useProfileAlert } from "./useProfileAlert";
import { useStudentPolygon } from "./useStudentPolygon";

export default function StudentOverviewTab() {
   const {
      studentId,
      isStaffViewer,
      studentPolygonDistrict,
      onPolygonSaved,
      canEditStudentProfile,
      formData,
      handleChange,
      canShowSensitiveProfileFields,
      groupsStatus,
      availableGroupsForSelect,
      absText,
      extrasSaving,
      extrasReady,
      onAbsFocus,
      onAbsChange,
      onAbsBlur,
      onAbsKeyDown,
      desiredInstructorId,
      desiredInstructorName,
      extrasForm,
      toggleField,
      groupsError,
      extrasError,
      saveError,
      saving,
      canDeleteStudent,
      deleteError,
      deleting,
      handleDelete,
      handleSave,
      resetProfileForm,
      profileRows,
   } = useContext(StudentInfoContext);

   // erorile filei: în bula de sus (AlertPills), nu sub câmpuri
   useProfileAlert(groupsStatus === "failed" ? groupsError : "");
   useProfileAlert(extrasError);
   useProfileAlert(saveError);
   useProfileAlert(deleteError);

   const polygon = useStudentPolygon({
      studentId: isStaffViewer ? studentId : null,
      value: studentPolygonDistrict,
      onSaved: onPolygonSaved,
   });
   useProfileAlert(polygon?.error);

   if (!canEditStudentProfile) {
      return (
         <>
            <InputUI.Group columns={2} className="studentsProfileUI__form">
               {profileRows.map((row) => (
                  <InputUI key={row.key} as="text" label={row.label} value={row.value} />
               ))}
            </InputUI.Group>
         </>
      );
   }

   const extrasOff = extrasSaving || !extrasReady;
   // plăcuța cu comutator: eticheta în stânga, comutatorul în dreapta (InputUI as="text" + after)
   const toggleTile = (field, label) => (
      <InputUI
         key={field}
         as="text"
         full
         label={label}
         after={
            <ToggleUI
               checked={extrasForm[field] === true}
               onChange={() => toggleField(field)}
               disabled={extrasOff}
               aria-label={label}
            />
         }
      />
   );

   const groupPlaceholder =
      groupsStatus === "loading"
         ? "Se încarcă grupele..."
         : availableGroupsForSelect.length > 0
           ? "Mută în altă grupă"
           : "Nu există grupe disponibile";

   return (
      <div className="studentsProfileUI__form">
         <InputUI.Group columns={2}>
            <InputUI label="Prenume" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Prenume" />
            <InputUI label="Nume" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Nume" />
            <InputUI label="Telefon" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="Telefon" />

            {canShowSensitiveProfileFields ? (
               <InputUI
                  as="select"
                  label="Grupă"
                  name="groupId"
                  value={formData.groupId}
                  onChange={handleChange}
                  disabled={groupsStatus === "loading"}
                  options={[
                     { value: "", label: groupPlaceholder },
                     ...availableGroupsForSelect.map((group) => ({ value: group.id, label: group?.name || "Grupă" })),
                  ]}
               />
            ) : null}

            {canShowSensitiveProfileFields ? (
               <InputUI label="IDNP" name="idnp" value={formData.idnp} onChange={handleChange} placeholder="IDNP" />
            ) : null}

            {canShowSensitiveProfileFields ? (
               <InputUI
                  label="Absențe"
                  name="number_of_absences"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={absText}
                  disabled={extrasOff}
                  onFocus={onAbsFocus}
                  onChange={onAbsChange}
                  onBlur={onAbsBlur}
                  onKeyDown={onAbsKeyDown}
               />
            ) : null}

            {desiredInstructorId ? <InputUI as="text" label="Instructor preferat" value={desiredInstructorName || "—"} /> : null}

            {polygon ? (
               <InputUI
                  as="select"
                  label="Poligon"
                  value={polygon.current}
                  onChange={(e) => polygon.save(e.target.value)}
                  disabled={polygon.busy}
                  options={polygon.options}
               />
            ) : null}

            <InputUI full label="Email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email" />

            {canShowSensitiveProfileFields ? toggleTile("medical_documents", "Doc. medicale") : null}
            {canShowSensitiveProfileFields ? toggleTile("individual_work", "Lucru individual") : null}
            {canShowSensitiveProfileFields ? toggleTile("canBookPracticalExam", "Permite Ex. practic") : null}
            {isStaffViewer ? toggleTile("fromOtherSchool", "Din altă școală") : null}
         </InputUI.Group>

         <ButtonUI.Row>
            {canDeleteStudent ? (
               <ButtonUI
                  variant="danger"
                  disabled={saving || deleting}
                  confirm={deleting ? "Se șterge..." : "Da, șterge"}
                  confirmTitle={ACCOUNT_DELETE_CONFIRM_TITLE}
                  cancelLabel="Nu"
                  onClick={handleDelete}
               >
                  {deleting ? "Se șterge..." : "Șterge"}
               </ButtonUI>
            ) : null}
            <ButtonUI variant="primary" onClick={handleSave} disabled={saving} loading={saving}>
               {saving ? "Salvez..." : "Salvează"}
            </ButtonUI>
            <ButtonUI onClick={resetProfileForm} disabled={saving}>
               Resetează
            </ButtonUI>
         </ButtonUI.Row>
      </div>
   );
}
